import { supabase } from "../../services/SupabaseService";
import { getCurrentUserId } from "../../utils/helpers";
import { ProjectLanguage } from "../../entities/ProjectLanguage";

class ProjectLanguagesRepository {
  private getUserId(): string {
    const id = getCurrentUserId();
    if (!id) throw new Error("User not authenticated");
    return id;
  }

  async getByProject(projectId: string): Promise<ProjectLanguage[]> {
    const userId = this.getUserId();
    const { data, error } = await supabase
      .from("project_languages")
      .select("*")
      .eq("project_id", projectId)
      .eq("user_id", userId)
      .order("language_code", { ascending: true });

    if (error) throw new Error(error.message);
    return (data || []).map(ProjectLanguage.fromRow);
  }

  async add(projectId: string, languageCode: string): Promise<ProjectLanguage> {
    const userId = this.getUserId();
    const normalizedCode = languageCode.trim().toLowerCase();

    const { data, error } = await supabase
      .from("project_languages")
      .insert({ project_id: projectId, language_code: normalizedCode, user_id: userId })
      .select()
      .single();

    if (error) {
      // Duplicate key error
      if (error.code === "23505") {
        throw new Error("This language is already in the project.");
      }
      throw new Error(error.message);
    }
    if (!data) throw new Error("Language could not be added.");
    return ProjectLanguage.fromRow(data);
  }

  /** Hard-delete: removes the language from the project's active set. */
  async remove(projectId: string, languageCode: string): Promise<void> {
    const userId = this.getUserId();
    const { error } = await supabase
      .from("project_languages")
      .delete()
      .eq("project_id", projectId)
      .eq("language_code", languageCode)
      .eq("user_id", userId);

    if (error) throw new Error(error.message);
  }
}

export default ProjectLanguagesRepository;
